import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { Plus, Upload, Download, Link2, Unlink } from 'lucide-react';
import toast from 'react-hot-toast';

const todayISO = () => new Date().toISOString().slice(0, 10);
const TEMPLATE_HEADERS = ['Date', 'Description', 'Deposit', 'Withdrawal'];

const AddLineModal = ({ accountId, onClose, onDone }) => {
  const [date, setDate] = useState(todayISO());
  const [description, setDescription] = useState('');
  const [type, setType] = useState('deposit');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = Number(amount);
    if (!(value > 0)) return toast.error('Enter an amount greater than 0');
    setSaving(true);
    try {
      await accountsApi.post('/bank-statement-lines', {
        account_id: accountId,
        statement_date: date,
        description: description || undefined,
        deposit_amount: type === 'deposit' ? value : 0,
        withdrawal_amount: type === 'withdrawal' ? value : 0,
      });
      toast.success('Statement line added');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add statement line');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
          <h2 className="text-lg font-bold">Add Statement Line</h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
            <input type="date" required value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={type === 'deposit'} onChange={() => setType('deposit')} /> Deposit
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={type === 'withdrawal'} onChange={() => setType('withdrawal')} /> Withdrawal
            </label>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
            <input type="number" step="0.01" min="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
              {saving ? 'Saving...' : 'Add Line'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BulkUploadModal = ({ accountId, onClose, onDone }) => {
  const [rows, setRows] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    const example = [
      { Date: todayISO(), Description: 'Example deposit', Deposit: 5000, Withdrawal: '' },
      { Date: todayISO(), Description: 'Example withdrawal', Deposit: '', Withdrawal: 1200 },
    ];
    const ws = XLSX.utils.json_to_sheet(example, { header: TEMPLATE_HEADERS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Statement Lines');
    XLSX.writeFile(wb, 'bank-statement-template.xlsx');
  };

  const validateRow = (row) => {
    const errors = [];
    const rawDate = row['Date'];
    let date = null;
    if (!rawDate) {
      errors.push('Date missing');
    } else if (rawDate instanceof Date) {
      date = rawDate.toISOString().slice(0, 10);
    } else {
      const parsed = new Date(rawDate);
      if (isNaN(parsed.getTime())) errors.push('Date is not valid');
      else date = parsed.toISOString().slice(0, 10);
    }

    const deposit = Number(row['Deposit']) || 0;
    const withdrawal = Number(row['Withdrawal']) || 0;
    if (deposit < 0 || withdrawal < 0) errors.push('Amounts cannot be negative');
    if ((deposit > 0) === (withdrawal > 0)) errors.push('Exactly one of Deposit or Withdrawal must be greater than 0');

    return {
      raw: row,
      valid: errors.length === 0,
      errors,
      fields: errors.length === 0 ? {
        statement_date: date,
        description: row['Description'] ? String(row['Description']).trim() : undefined,
        deposit_amount: deposit,
        withdrawal_amount: withdrawal,
      } : null,
    };
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResults(null);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      if (json.length === 0) {
        toast.error('No rows found in the file');
        setRows(null);
        return;
      }
      setRows(json.map(validateRow));
    } catch (err) {
      toast.error('Could not read that file — is it a valid .xlsx?');
      setRows(null);
    }
  };

  const handleImport = async () => {
    const validRows = rows.filter(r => r.valid);
    if (validRows.length === 0) return toast.error('No valid rows to import');
    setImporting(true);
    const outcomes = [];
    for (const r of validRows) {
      try {
        await accountsApi.post('/bank-statement-lines', { account_id: accountId, ...r.fields });
        outcomes.push({ ok: true, description: r.fields.description || r.fields.statement_date });
      } catch (err) {
        outcomes.push({ ok: false, description: r.fields.description || r.fields.statement_date, message: err.response?.data?.message || 'Failed to add' });
      }
    }
    setImporting(false);
    const successCount = outcomes.filter(o => o.ok).length;
    setResults({ successCount, failCount: outcomes.length - successCount, outcomes });
    if (successCount > 0) {
      toast.success(`${successCount} statement line(s) imported`);
      onDone();
    }
    if (outcomes.some(o => !o.ok)) {
      toast.error(`${outcomes.length - successCount} line(s) failed`);
    }
  };

  const validCount = rows ? rows.filter(r => r.valid).length : 0;
  const invalidCount = rows ? rows.length - validCount : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-bold flex items-center gap-2"><Upload size={18} /> Bulk Upload Statement Lines</h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="flex items-end gap-3">
            <button type="button" onClick={downloadTemplate}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
              <Download size={16} /> Download Template
            </button>
            <p className="text-xs text-gray-500">One row per statement line. Fill exactly one of Deposit or Withdrawal.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Upload filled-in template (.xlsx)</label>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFile}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-800 file:text-white file:text-sm hover:file:bg-blue-900" />
          </div>

          {rows && (
            <>
              <div className="flex gap-4 text-sm">
                <span className="text-green-700 font-medium">{validCount} valid row(s)</span>
                {invalidCount > 0 && <span className="text-red-600 font-medium">{invalidCount} with errors (won't be imported)</span>}
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Row</th>
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Date</th>
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Description</th>
                      <th className="text-right px-3 py-2 text-gray-600 font-semibold">Deposit</th>
                      <th className="text-right px-3 py-2 text-gray-600 font-semibold">Withdrawal</th>
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className={`border-t border-gray-100 ${r.valid ? '' : 'bg-red-50'}`}>
                        <td className="px-3 py-1.5 text-gray-500">{i + 2}</td>
                        <td className="px-3 py-1.5 text-gray-700">{String(r.raw['Date'])}</td>
                        <td className="px-3 py-1.5 text-gray-700">{r.raw['Description']}</td>
                        <td className="px-3 py-1.5 text-right text-gray-700">{r.raw['Deposit']}</td>
                        <td className="px-3 py-1.5 text-right text-gray-700">{r.raw['Withdrawal']}</td>
                        <td className={`px-3 py-1.5 ${r.valid ? 'text-green-700' : 'text-red-600'}`}>
                          {r.valid ? 'Valid' : r.errors.join('; ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {results && (
            <div className={`rounded-lg p-3 text-sm ${results.failCount === 0 ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-yellow-50 border border-yellow-200 text-yellow-800'}`}>
              Imported {results.successCount} of {results.successCount + results.failCount} line(s).
              {results.failCount > 0 && ` Failed: ${results.outcomes.filter(o => !o.ok).map(o => `${o.description} (${o.message})`).join(', ')}`}
            </div>
          )}
        </div>
        <div className="flex gap-3 p-6 pt-0 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
          <button type="button" onClick={handleImport} disabled={!rows || validCount === 0 || importing}
            className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
            {importing ? 'Importing...' : `Import ${validCount} Line(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

const MatchModal = ({ line, outstandingBookLines, onClose, onDone }) => {
  const [suggested, setSuggested] = useState(null);
  const [matching, setMatching] = useState(false);

  useEffect(() => {
    accountsApi.get(`/bank-statement-lines/${line.id}/suggested-matches`)
      .then(res => setSuggested(res.data.suggested_matches))
      .catch(() => {
        toast.error('Failed to load suggested matches');
        setSuggested([]);
      });
  }, [line.id]);

  const targetAmount = Number(line.deposit_amount) > 0 ? Number(line.deposit_amount) : Number(line.withdrawal_amount);
  const targetColumn = Number(line.deposit_amount) > 0 ? 'debit' : 'credit';
  const fallbackCandidates = (outstandingBookLines || []).filter(b => Number(b[targetColumn]) === targetAmount);

  const doMatch = async (journalEntryLineId) => {
    setMatching(true);
    try {
      await accountsApi.post(`/bank-statement-lines/${line.id}/match`, { journal_entry_line_id: journalEntryLineId });
      toast.success('Matched');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to match');
    } finally {
      setMatching(false);
    }
  };

  const candidates = suggested && suggested.length > 0 ? suggested : fallbackCandidates;
  const isFallback = suggested && suggested.length === 0 && fallbackCandidates.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-bold">
            Match Statement Line — {line.description || line.statement_date?.slice(0, 10)} ({targetAmount.toFixed(2)})
          </h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-6 space-y-3 overflow-y-auto">
          {suggested === null ? (
            <div className="text-center py-6 text-gray-500 text-sm">Loading suggested matches...</div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No suggested matches within 14 days, and no other outstanding book entries with this exact amount.</div>
          ) : (
            <>
              {isFallback && (
                <p className="text-xs text-amber-600">No suggested matches within 14 days — showing all outstanding book entries with a matching amount instead.</p>
              )}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Date</th>
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Description</th>
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Voucher</th>
                      <th className="text-right px-3 py-2 text-gray-600 font-semibold">Amount</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map(c => (
                      <tr key={c.journal_entry_line_id} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-700">{c.entry_date?.slice(0, 10)}</td>
                        <td className="px-3 py-2 text-gray-700">{c.line_description || c.narration || '—'}</td>
                        <td className="px-3 py-2 text-gray-700">{c.voucher_no || '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-800">{Number(c.debit || c.credit).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => doMatch(c.journal_entry_line_id)} disabled={matching}
                            className="text-blue-700 hover:text-blue-900 text-sm font-medium disabled:opacity-50">Match</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="flex gap-3 p-6 pt-0 flex-shrink-0">
          <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
        </div>
      </div>
    </div>
  );
};

const MatchedLinesPanel = ({ accountId, onClose, onChanged }) => {
  const [lines, setLines] = useState(null);
  const [unmatching, setUnmatching] = useState(null);

  const fetchMatched = async () => {
    try {
      const res = await accountsApi.get('/bank-statement-lines', { params: { account_id: accountId, status: 'Matched' } });
      setLines(res.data.bank_statement_lines);
    } catch (err) {
      toast.error('Failed to load matched lines');
    }
  };

  useEffect(() => { fetchMatched(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUnmatch = async (id) => {
    setUnmatching(id);
    try {
      await accountsApi.post(`/bank-statement-lines/${id}/unmatch`);
      toast.success('Unmatched');
      fetchMatched();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to unmatch');
    } finally {
      setUnmatching(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-bold">Matched Statement Lines</h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-6 overflow-y-auto">
          {lines === null ? (
            <div className="text-center py-6 text-gray-500 text-sm">Loading...</div>
          ) : lines.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No matched lines yet</div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="text-left px-3 py-2 text-gray-600 font-semibold">Date</th>
                    <th className="text-left px-3 py-2 text-gray-600 font-semibold">Description</th>
                    <th className="text-right px-3 py-2 text-gray-600 font-semibold">Deposit</th>
                    <th className="text-right px-3 py-2 text-gray-600 font-semibold">Withdrawal</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(l => (
                    <tr key={l.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-700">{l.statement_date?.slice(0, 10)}</td>
                      <td className="px-3 py-2 text-gray-700">{l.description || '—'}</td>
                      <td className="px-3 py-2 text-right text-gray-800">{Number(l.deposit_amount) > 0 ? Number(l.deposit_amount).toFixed(2) : '—'}</td>
                      <td className="px-3 py-2 text-right text-gray-800">{Number(l.withdrawal_amount) > 0 ? Number(l.withdrawal_amount).toFixed(2) : '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => handleUnmatch(l.id)} disabled={unmatching === l.id}
                          className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50 flex items-center gap-1 justify-end w-full">
                          <Unlink size={14} /> Unmatch
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="flex gap-3 p-6 pt-0 flex-shrink-0">
          <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
        </div>
      </div>
    </div>
  );
};

const BankReconciliation = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [asOfDate, setAsOfDate] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showMatchedPanel, setShowMatchedPanel] = useState(false);
  const [matchLine, setMatchLine] = useState(null);

  useEffect(() => {
    accountsApi.get('/chart-of-accounts')
      .then(res => {
        const bankable = res.data.accounts.filter(a => a.is_cash_or_bank);
        setAccounts(bankable);
        if (bankable.length > 0) setAccountId(String(bankable[0].id));
      })
      .catch(() => toast.error('Failed to load accounts'));
  }, []);

  const runReport = async () => {
    if (!accountId) return;
    try {
      setLoading(true);
      const res = await accountsApi.get('/bank-reconciliation/summary', { params: { account_id: accountId, as_of_date: asOfDate } });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load reconciliation summary');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (accountId) runReport(); }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout title="Bank Reconciliation">
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Account</label>
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {accounts.length === 0 && <option value="">No cash/bank accounts</option>}
              {accounts.map(a => <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">As Of</label>
            <input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <button onClick={runReport} className="bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
            Apply
          </button>
          <div className="flex-1" />
          <button onClick={() => setShowMatchedPanel(true)} disabled={!accountId}
            className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
            <Link2 size={16} /> Matched Lines
          </button>
          <button onClick={() => setShowBulkModal(true)} disabled={!accountId}
            className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
            <Upload size={16} /> Bulk Upload
          </button>
          <button onClick={() => setShowAddModal(true)} disabled={!accountId}
            className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
            <Plus size={16} /> Add Statement Line
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : !data ? null : (
        <>
          <div className={`rounded-xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3 ${data.reconciled ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <span className={`font-semibold ${data.reconciled ? 'text-green-700' : 'text-red-700'}`}>
              {data.reconciled ? '✓ Reconciled' : '✗ Mismatch'}
            </span>
            <div className="flex gap-6 text-sm">
              <span className="text-gray-600">Book Balance: <span className="font-semibold text-gray-800">{data.book_balance.toFixed(2)}</span></span>
              <span className="text-gray-600">Statement Balance: <span className="font-semibold text-gray-800">{data.statement_balance.toFixed(2)}</span></span>
              <span className="text-gray-600">Difference: <span className="font-semibold text-gray-800">{data.difference.toFixed(2)}</span></span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-700 text-sm">Outstanding Bank Lines</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-3 py-2 text-gray-600 font-semibold">Date</th>
                    <th className="text-left px-3 py-2 text-gray-600 font-semibold">Description</th>
                    <th className="text-right px-3 py-2 text-gray-600 font-semibold">Deposit</th>
                    <th className="text-right px-3 py-2 text-gray-600 font-semibold">Withdrawal</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.outstanding_bank_lines.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-6 text-gray-500">Nothing outstanding</td></tr>
                  ) : data.outstanding_bank_lines.map(l => (
                    <tr key={l.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-700">{l.statement_date?.slice(0, 10)}</td>
                      <td className="px-3 py-2 text-gray-700">{l.description || '—'}</td>
                      <td className="px-3 py-2 text-right text-gray-800">{Number(l.deposit_amount) > 0 ? Number(l.deposit_amount).toFixed(2) : '—'}</td>
                      <td className="px-3 py-2 text-right text-gray-800">{Number(l.withdrawal_amount) > 0 ? Number(l.withdrawal_amount).toFixed(2) : '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => setMatchLine(l)} className="text-blue-700 hover:text-blue-900 text-sm font-medium">Match</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-700 text-sm">Outstanding Book Lines</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-3 py-2 text-gray-600 font-semibold">Date</th>
                    <th className="text-left px-3 py-2 text-gray-600 font-semibold">Description</th>
                    <th className="text-left px-3 py-2 text-gray-600 font-semibold">Voucher</th>
                    <th className="text-right px-3 py-2 text-gray-600 font-semibold">Debit</th>
                    <th className="text-right px-3 py-2 text-gray-600 font-semibold">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.outstanding_book_lines.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-6 text-gray-500">Nothing outstanding</td></tr>
                  ) : data.outstanding_book_lines.map(l => (
                    <tr key={l.journal_entry_line_id} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-700">{l.entry_date?.slice(0, 10)}</td>
                      <td className="px-3 py-2 text-gray-700">{l.line_description || l.narration || '—'}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {l.voucher_id ? <Link to={`/vouchers/${l.voucher_id}`} className="text-blue-700 hover:underline">{l.voucher_no}</Link> : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-800">{Number(l.debit) > 0 ? Number(l.debit).toFixed(2) : '—'}</td>
                      <td className="px-3 py-2 text-right text-gray-800">{Number(l.credit) > 0 ? Number(l.credit).toFixed(2) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showAddModal && (
        <AddLineModal accountId={Number(accountId)} onClose={() => setShowAddModal(false)}
          onDone={() => { setShowAddModal(false); runReport(); }} />
      )}
      {showBulkModal && (
        <BulkUploadModal accountId={Number(accountId)} onClose={() => setShowBulkModal(false)}
          onDone={() => { setShowBulkModal(false); runReport(); }} />
      )}
      {showMatchedPanel && (
        <MatchedLinesPanel accountId={Number(accountId)} onClose={() => setShowMatchedPanel(false)}
          onChanged={runReport} />
      )}
      {matchLine && (
        <MatchModal line={matchLine} outstandingBookLines={data?.outstanding_book_lines}
          onClose={() => setMatchLine(null)}
          onDone={() => { setMatchLine(null); runReport(); }} />
      )}
    </Layout>
  );
};

export default BankReconciliation;
